import { Link } from 'react-router'
import { Button } from '#app/components/ui/button.tsx'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '#app/components/ui/card.tsx'
import { Icon } from '#app/components/ui/icon.tsx'

export default function AboutRoute() {
  return (
    <>
      <main className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto space-y-12">
          {/* Header Section */}
          <div className="text-center space-y-6">
            <h1 className="font-brand tracking-tight text-4xl md:text-5xl font-bold text-foreground">
              About Youth Rollcall
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              We're passionate about making attendance tracking simple, efficient, and meaningful for youth programs everywhere.
            </p>
          </div>

          {/* Mission Section */}
          <div className="bg-muted rounded-xl border p-10">
            <h2 className="text-3xl font-bold mb-6 text-center">Our Mission</h2>
            <p className="text-lg text-muted-foreground text-center max-w-3xl mx-auto">
              To empower youth program leaders with intuitive tools that help them focus on what matters most -
              building relationships and creating meaningful experiences for young people.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 gap-8">
            <Card>
              <CardHeader>
                <div className="w-12 h-12 bg-muted border rounded-lg flex items-center justify-center mb-4">
                  <Icon name="check" className="h-6 w-6 text-brand" />
                </div>
                <CardTitle>Mobile-First Design</CardTitle>
                <CardDescription>
                  Built for the modern world where teachers and program leaders are always on the move.
                  Take attendance from anywhere with our responsive, touch-friendly interface.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card>
              <CardHeader>
                <div className="w-12 h-12 bg-muted border rounded-lg flex items-center justify-center mb-4">
                  <Icon name="file-text" className="h-6 w-6 text-brand" />
                </div>
                <CardTitle>Smart Analytics</CardTitle>
                <CardDescription>
                  Get insights that matter. Track attendance patterns, identify trends, and generate
                  comprehensive reports to help improve your programs.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card>
              <CardHeader>
                <div className="w-12 h-12 bg-muted border rounded-lg flex items-center justify-center mb-4">
                  <Icon name="lock-closed" className="h-6 w-6 text-brand" />
                </div>
                <CardTitle>Secure & Private</CardTitle>
                <CardDescription>
                  Your data is protected with enterprise-grade security. We follow strict privacy
                  guidelines to ensure student information remains confidential.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card>
              <CardHeader>
                <div className="w-12 h-12 bg-muted border rounded-lg flex items-center justify-center mb-4">
                  <Icon name="update" className="h-6 w-6 text-brand" />
                </div>
                <CardTitle>Always Improving</CardTitle>
                <CardDescription>
                  We listen to our users and continuously improve the platform based on real-world
                  feedback from program leaders and teachers.
                </CardDescription>
              </CardHeader>
            </Card>
          </div>

          {/* Story Section */}
          <div className="space-y-8">
            <h2 className="text-3xl font-bold text-center">Our Story</h2>
            <div className="prose prose-lg max-w-none text-muted-foreground">
              <p>
                Youth Rollcall was born from a simple observation: too many youth programs were spending
                valuable time on administrative tasks instead of focusing on their core mission of
                supporting young people.
              </p>
              <p>
                We saw teachers and program leaders struggling with outdated attendance systems,
                paper-based tracking, and complex software that didn't understand the unique needs
                of youth programs. That's when we decided to build something better.
              </p>
              <p>
                Today, Youth Rollcall serves programs across the country, from small community centers
                to large school districts. We're proud to be part of the solution that helps youth
                programs run more efficiently and effectively.
              </p>
            </div>
          </div>

          {/* Values Section */}
          <div className="bg-muted rounded-xl border p-10">
            <h2 className="text-3xl font-bold mb-8 text-center">Our Values</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <h3 className="text-xl font-bold mb-3">Simplicity</h3>
                <p className="text-muted-foreground">
                  We believe technology should make life easier, not more complicated.
                </p>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold mb-3">Reliability</h3>
                <p className="text-muted-foreground">
                  Your program depends on us, so we're committed to being there when you need us.
                </p>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold mb-3">Impact</h3>
                <p className="text-muted-foreground">
                  Every feature we build is designed to help you make a positive impact on young lives.
                </p>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center space-y-6">
            <h2 className="text-3xl font-bold">Ready to Get Started?</h2>
            <p className="text-lg text-muted-foreground">
              Join the growing community of youth programs using Youth Rollcall to streamline their operations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg">
                <Link to="/">
                  Back to Home
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </main>
    </>
  )
}
